import React from 'react';
import { enUS } from 'date-fns/locale';
import styled, { createGlobalStyle } from 'styled-components';
import _ from 'lodash';

import { DatePicker as NiceDatePicker } from '@axel-dev/react-nice-dates';
import '@axel-dev/react-nice-dates/build/style.css';

import { Container, TextInput, ClearIcon } from 'ui/components/styled';
import { spacing } from 'ui/theme/utils';

export const DatePicker = ({
  width = '200px',
  placeholder = 'Choose a date',
  popoverWidth = '230px',
  value,
  onChange,
  format,
  clearValue = '',
  isClearable,
  ...rest
}) => {
  const date = _.isDate(value) ? value : null;
  const inputFocused = React.useRef(false);

  const delayedChange = React.useCallback(
    _.debounce((v) => onChange(v), 700),
    [onChange]
  );

  const handleChange = (v) => {
    if (inputFocused.current) {
      return delayedChange(v);
    } else {
      return onChange(v);
    }
  };

  const handleInputFocus = (event, callback) => {
    inputFocused.current = true;
    callback(event);
  };

  const handleInputBlur = (event, callback) => {
    inputFocused.current = false;
    callback(event);
  };

  const handleClear = () => {
    onChange(clearValue);
  };

  return (
    <>
      <DatePickerStyle width={popoverWidth} />
      <NiceDatePicker date={date} onDateChange={handleChange} format={format} locale={enUS}>
        {({ inputProps }) => (
          <RelativeContainer {...rest} width={width} height="auto">
            <CalendarIcon />
            <TextInput
              variant="minimal"
              textAlign="center"
              cursor="initial"
              px="40px"
              {...inputProps}
              onFocus={(event) => handleInputFocus(event, inputProps.onFocus)}
              onBlur={(event) => handleInputBlur(event, inputProps.onBlur)}
              placeholder={placeholder}
            />
            {isClearable && value && <ClearIcon onClick={handleClear} />}
          </RelativeContainer>
        )}
      </NiceDatePicker>
    </>
  );
};

const DatePickerStyle = createGlobalStyle`
  .nice-dates {
    position: relative;
  }

  .nice-dates-day {
    &::before {
      background-color: ${({ theme }) => theme.colors.primary};
    }
    &::after {
      border: none;
      background-color: #e7edff;
    }
    &::before,
    &::after {
      top: 3px;
      left: 3px;
      right: 3px;
      bottom: 3px;
      border-radius: 50%;
    }
    &.-today {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  .nice-dates-popover {
    width: ${({ width = 'auto' }) => width};
  }

  .nice-dates-day_month {
    display: none;
  }
`;

const RelativeContainer = styled(Container)`
  position: relative;
  ${(props) => spacing(props)}
`;

const CalendarIcon = styled.img.attrs(() => ({
  src: '/assets/img/icons/calendar.svg',
  alt: 'calendar',
}))`
  position: absolute;
  top: 8px;
  left: 10px;
  width: 18px;
  pointer-events: none;
`;
